import React, { useState } from "react";
import {
  Alert,
  Card,
  CardContent,
  CircularProgress,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import * as SC from "SC";
import { httpService } from "api/httpService";

const VEHICLE_TYPES = [
  { value: "SEDAN", label: "Sedan" },
  { value: "WHEELCHAIR_VAN", label: "Wheelchair Van" },
  { value: "STRETCHER", label: "Stretcher" },
  { value: "SUV", label: "SUV" },
  { value: "VAN", label: "Van" },
];

const EMPTY_FORM = {
  vin: "",
  make: "",
  model: "",
  year: "",
  vehicleType: "SEDAN",
  capacity: "",
};

export const VehicleStep = ({ onDone, onSkip }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState({ loading: false, error: null });

  const update = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const submit = async () => {
    if (!form.make.trim() || !form.model.trim()) {
      setStatus({ loading: false, error: "Make and model are required" });
      return;
    }
    setStatus({ loading: true, error: null });

    const payload = {
      vin: form.vin.trim() || null,
      make: form.make.trim(),
      model: form.model.trim(),
      year: form.year ? Number(form.year) : null,
      vehicleType: form.vehicleType,
      capacity: form.capacity ? Number(form.capacity) : null,
    };

    const { value, error } = await httpService({
      url: "/vehicles",
      options: { method: "POST", data: payload },
    });

    if (error) {
      if (error.toLowerCase().includes("unauthorized")) {
        window.sessionStorage.setItem(
          "fleetmd.onboarding.vehicle",
          JSON.stringify(payload)
        );
        setStatus({
          loading: false,
          error:
            "Saved locally — we'll finish creating this vehicle the first time you log in.",
        });
        setTimeout(onDone, 900);
        return;
      }
      setStatus({ loading: false, error });
      return;
    }

    setStatus({ loading: false, error: null });
    onDone(value);
  };

  return (
    <Stack gap={2.5}>
      <Stack>
        <SC.AuthTitle sx={{ fontSize: "1.4rem", fontWeight: 700 }}>
          Add your first vehicle
        </SC.AuthTitle>
        <Typography variant="body2" color="text.secondary">
          You can add more from your fleet dashboard later.
        </Typography>
      </Stack>

      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent>
          <Stack gap={2}>
            <Stack gap={0.5}>
              <SC.InputLabel htmlFor="vin">VIN</SC.InputLabel>
              <OutlinedInput
                id="vin"
                size="small"
                value={form.vin}
                onChange={update("vin")}
                placeholder="1HGCM82633A123456"
                fullWidth
              />
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              <Stack gap={0.5} flex={1}>
                <SC.InputLabel htmlFor="make">Make *</SC.InputLabel>
                <OutlinedInput
                  id="make"
                  size="small"
                  value={form.make}
                  onChange={update("make")}
                  placeholder="Ford"
                  fullWidth
                />
              </Stack>
              <Stack gap={0.5} flex={1}>
                <SC.InputLabel htmlFor="model">Model *</SC.InputLabel>
                <OutlinedInput
                  id="model"
                  size="small"
                  value={form.model}
                  onChange={update("model")}
                  placeholder="Transit"
                  fullWidth
                />
              </Stack>
            </Stack>
            <Stack direction={{ xs: "column", sm: "row" }} gap={2}>
              <Stack gap={0.5} flex={1}>
                <SC.InputLabel htmlFor="year">Year</SC.InputLabel>
                <OutlinedInput
                  id="year"
                  size="small"
                  type="number"
                  value={form.year}
                  onChange={update("year")}
                  placeholder="2022"
                  fullWidth
                />
              </Stack>
              <Stack gap={0.5} flex={1}>
                <SC.InputLabel htmlFor="vehicleType">Vehicle Type *</SC.InputLabel>
                <Select
                  id="vehicleType"
                  size="small"
                  value={form.vehicleType}
                  onChange={update("vehicleType")}
                  input={<OutlinedInput size="small" />}
                >
                  {VEHICLE_TYPES.map((t) => (
                    <MenuItem key={t.value} value={t.value}>
                      {t.label}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
              <Stack gap={0.5} flex={1}>
                <SC.InputLabel htmlFor="capacity">Capacity</SC.InputLabel>
                <OutlinedInput
                  id="capacity"
                  size="small"
                  type="number"
                  value={form.capacity}
                  onChange={update("capacity")}
                  placeholder="4"
                  fullWidth
                />
              </Stack>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {status.error && <Alert severity="warning">{status.error}</Alert>}

      <Stack direction="row" justifyContent="space-between" gap={1.5}>
        <SC.ControlButton variant="outlined" onClick={onSkip} disabled={status.loading}>
          Skip for now
        </SC.ControlButton>
        <SC.ControlButton
          variant="contained"
          onClick={submit}
          disabled={status.loading}
          sx={{ minWidth: 180 }}
        >
          {status.loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            "Save & Continue →"
          )}
        </SC.ControlButton>
      </Stack>
    </Stack>
  );
};

export default VehicleStep;
